// 환자 자동관리 ② 「발송 예정」 (2026-10-07, 기획서_환자자동관리_통합화면 v3 §3-2 · 대표 결정 Q1-a: 하단 접힘 「최근 7일 발송」)
//  - today + week 병합(키 pid|kind|refId|day, today 우선). D-day = day − 서버 todayKey(브라우저 날짜 금지 — 계약 §5). 정렬 D-day → 종류 → 환자명 → refId.
//  - 머리: 10시 전 texts.boardTodayBefore + previewNotice / 10시 후 texts.boardTodayAfter — 기준은 서버 generatedAt(isAfterDispatch).
//  - roadmapOn=false 면 kind 'roadmap' 행 제거(②·최근 7일 블록 모두 — 법무 P2-7). 문구는 서버 texts·labels·행 label 그대로(웹 사전 없음).
//  - D-day 칩 전부 회색(D-0 만 굵게). 금지: 점수·등급·경보색·편집/재발송/수동발송·내보내기·인쇄·CSV·복사.
import React from 'react';
import { unifiedStyles as U } from '../../styles/Styles';
import { NOTICES_SINCE_DATE, isAfterDispatch, dispatchTimeOf, lastDispatchTimeOf, fmtDayKey, fmtAt } from '../../services/clinicNoticesApi';

const thStyle = { ...U.tableTh, padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' };
const tdStyle = { ...U.tableTd, padding: '8px 10px', color: '#334155', verticalAlign: 'top' };
const smallBtn = { ...U.buttonSecondary, padding: '3px 10px', fontSize: 12 };
const linkBtn = { background: 'transparent', border: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: '#4338ca', cursor: 'pointer', textDecoration: 'underline' };
const ddChip = { display: 'inline-block', minWidth: 38, textAlign: 'center', borderRadius: 6, padding: '3px 8px', fontSize: 12, fontWeight: 600, background: '#f1f5f9', color: '#475569' };
const NOT_YET_TEXT = '아직 안 나간 것';   // ☆ D-0 행 비고(10시 이후) — 법무 10-07 승인
const LAST7_SUFFIX = '건 · 전달 결과 포함';   // 최근 7일 접힘 머리 건수 보조
const KIND_ORDER = { roadmap: 0, feedback: 1, recall: 2, clinic_notice: 3 };
// 2026-10-08 알림 파트: 치과 안내(kind 'clinic_notice') 행 — 라벨은 서버 행 label 우선, 없을 때만 이 폴백.
//   계약_알림파트_서버 §C: 보드 today/week = 안내 1건 1행 {pid:null, name:'', targetCount}, last7 = 환자별 행(pid 있음). 둘 다 표시한다.
const NOTICE_KIND = 'clinic_notice';
const NOTICE_LABEL_FALLBACK = '치과 안내';
const NOTICE_NOTE_TEXT = '오전 10시 20분 발송 · 하루 1개 규칙·휴진일로 미뤄질 수 있음';
export const rowLabelOf = (r) => (r && r.label) || (r && r.kind === NOTICE_KIND ? NOTICE_LABEL_FALLBACK : '');

const DAY_MS = 24 * 60 * 60 * 1000;
const dayKeyMs = (key) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || '')); return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : NaN; };
export function ddayOf(day, todayKey) {
    const a = dayKeyMs(day), b = dayKeyMs(todayKey);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return Math.round((a - b) / DAY_MS);
}

// today + week 병합(순수)
export function mergeUpcoming(todayRows, weekRows, todayKey, roadmapOn) {
    const seen = new Set();
    const out = [];
    for (const r of [...(todayRows || []), ...(weekRows || [])]) {
        if (!r || !r.day) continue;
        if (!r.pid && !(r.kind === NOTICE_KIND && r.refId)) continue;   // pid 없는 행은 치과 안내 1건 1행만 허용
        if (r.preview !== true) continue;
        if (roadmapOn === false && r.kind === 'roadmap') continue;
        const key = `${r.pid || '-'}|${r.kind}|${r.refId || ''}|${r.day}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ ...r, key, dday: ddayOf(r.day, todayKey) });
    }
    out.sort((a, b) => ((a.dday ?? 999) - (b.dday ?? 999)) || ((KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9))
        || String(a.name || '').localeCompare(String(b.name || ''), 'ko') || String(a.refId || '').localeCompare(String(b.refId || '')));
    return out;
}

// 보조 — 머리 문구·빈 상태(전부 서버 texts)
export function upcomingHeadOf(d) {
    const t = d?.texts || {};
    // 머리 문구는 가장 늦은 종류(안내 10:20)까지 나간 뒤 전환 — dispatchTimes 없으면 기존 dispatchHour
    const after = isAfterDispatch({ generatedAt: d?.generatedAt, todayKey: d?.todayKey, dispatchHour: d?.dispatchHour, dispatchTime: lastDispatchTimeOf(d) });
    return { after, text: after ? (t.boardTodayAfter || '') : (t.boardTodayBefore || ''), preview: after ? '' : (d?.previewNotice || '') };
}

function ClinicUpcomingList({ board, roadmapOn, navigate, last7Open, onToggleLast7 }) {
    const today = board.state.today, week = board.state.week, last7 = board.state.last7;
    const ref = today.data || week.data;   // todayKey·texts 는 두 응답이 같다 — 하나라도 오면 사용
    const todayKey = ref?.todayKey || '';
    const texts = ref?.texts || {};
    const rows = mergeUpcoming(today.data?.rows, week.data?.rows, todayKey, roadmapOn);
    const head = upcomingHeadOf(today.data || week.data);
    const loading = today.loading || week.loading;
    const partial = (today.data?.errors?.length > 0 || week.data?.errors?.length > 0) && texts.partialError;
    const truncated = today.data?.truncated || week.data?.truncated;

    const noteOf = (r) => {
        // D-0 행은 행 종류별 발송 시각(dispatchTimes[kind]) 기준 — 10:00~10:20 사이 리콜·안내가 "아직 안 나간 것"으로 보이던 문제(계약 §C-2)
        if (r.dday === 0 && isAfterDispatch({ generatedAt: ref?.generatedAt, todayKey: ref?.todayKey, dispatchHour: ref?.dispatchHour, dispatchTime: dispatchTimeOf(ref, r.kind) })) return NOT_YET_TEXT;
        if (r.kind === 'roadmap' || r.kind === 'recall') return texts.upcomingNote || '';
        if (r.kind === NOTICE_KIND) return NOTICE_NOTE_TEXT;   // 치과 안내 10:20 — 하루 1개 장부·휴진일로 미뤄질 수 있음(계약 B-6)
        return '';
    };

    // 최근 7일 발송(Q1-a) — 열: 환자명 | 종류 | 날짜(시각) | 결과 (구 ClinicNoticeBoard 와 동일)
    const l7rows = (last7.data?.rows || []).filter(r => !(roadmapOn === false && r.kind === 'roadmap'));
    const l7empty = () => {
        const t = last7.data?.texts || {};
        const since = t.emptyLast7Since ? ` ${t.emptyLast7Since.replace('{date}', NOTICES_SINCE_DATE)}` : '';
        return `${t.emptyLast7 || ''}${since}`;
    };

    return (
        <div style={{ ...U.card, padding: 0, overflow: 'hidden' }}>
            {(head.text || head.preview || partial) && (
                <div style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>
                    {head.text && <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{head.text}</span>}
                    {head.preview && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{head.preview}</div>}
                    {partial && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{texts.partialError}</div>}
                </div>
            )}
            {(today.error || week.error) && (
                <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, margin: 10 }}>
                    {today.error || week.error}
                    <button type="button" onClick={() => { if (today.error) board.loadOne('today'); if (week.error) board.loadOne('week'); }} style={{ ...smallBtn, marginLeft: 6 }}>다시 시도</button>
                </div>
            )}
            {loading && rows.length === 0 ? (
                <div style={{ padding: '16px 12px', fontSize: 13, color: '#94a3b8' }}>불러오는 중...</div>
            ) : !ref ? null : rows.length === 0 ? (
                <div style={{ padding: '16px 12px', fontSize: 13, color: '#94a3b8', lineHeight: 1.7 }}>
                    {texts.emptyToday && <div>{texts.emptyToday}</div>}
                    {texts.emptyWeek && <div>{texts.emptyWeek}</div>}
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ ...thStyle, width: 70 }}>D-day</th>
                                <th style={thStyle}>날짜</th>
                                <th style={thStyle}>환자명</th>
                                <th style={thStyle}>종류</th>
                                <th style={thStyle}>비고</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.key}>
                                    <td style={tdStyle}><span style={{ ...ddChip, fontWeight: r.dday === 0 ? 700 : 600 }}>{r.dday == null ? '-' : r.dday < 0 ? '지남' : `D-${r.dday}`}</span></td>
                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDayKey(r.day)}</td>
                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontWeight: 600, color: '#0f172a' }}>
                                        {r.pid ? (
                                            <button type="button" onClick={() => navigate(`/patients/${r.pid}#notices`)} style={linkBtn}>{r.name || '(이름 없음)'}</button>
                                        ) : (
                                            <button type="button" onClick={() => navigate('/patients/alerts#status')} style={linkBtn}>{Number.isFinite(r.targetCount) ? `대상 ${r.targetCount}명` : '대상 환자'}</button>
                                        )}
                                    </td>
                                    <td style={tdStyle}>
                                        <div style={{ color: '#0f172a' }}>{rowLabelOf(r)}</div>
                                        {r.stageLabel && <div style={{ fontSize: 12, color: '#64748b' }}>{r.stageLabel}</div>}
                                    </td>
                                    <td style={{ ...tdStyle, fontSize: 12.5, color: '#64748b' }}>{noteOf(r)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {truncated && <div style={{ padding: '6px 12px', fontSize: 12, color: '#94a3b8' }}>{'표시 한도를 넘어 일부 행이 보이지 않습니다.'}</div>}

            {/* Q1-a — 최근 7일 발송(전달 결과 포함) 접힘 블록 */}
            <div style={{ borderTop: '1px solid #e2e8f0' }}>
                <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" aria-expanded={last7Open} onClick={onToggleLast7} title={last7Open ? '접기' : '펼치기'}
                        style={{ width: 22, height: 22, borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', color: '#64748b', fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
                        {last7Open ? '▾' : '▸'}
                    </button>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', cursor: 'pointer' }} onClick={onToggleLast7}>{last7.data?.texts?.boardLast7 || texts.boardLast7 || '최근 7일 발송'}</span>
                    {last7.data && <span style={{ fontSize: 12, color: '#94a3b8' }}>{`${l7rows.length}${LAST7_SUFFIX}`}</span>}
                    {last7.data?.errors?.length > 0 && last7.data?.texts?.partialError && <span style={{ fontSize: 12, color: '#64748b' }}>{last7.data.texts.partialError}</span>}
                </div>
                {last7Open && (
                    <div>
                        {last7.error && (
                            <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, margin: 10 }}>
                                {last7.error} <button type="button" onClick={() => board.loadOne('last7')} style={{ ...smallBtn, marginLeft: 6 }}>다시 시도</button>
                            </div>
                        )}
                        {last7.loading ? (
                            <div style={{ padding: '16px 12px', fontSize: 13, color: '#94a3b8' }}>불러오는 중...</div>
                        ) : !last7.data ? null : l7rows.length === 0 ? (
                            <div style={{ padding: '16px 12px', fontSize: 13, color: '#94a3b8' }}>{l7empty()}</div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>환자명</th>
                                            <th style={thStyle}>종류</th>
                                            <th style={thStyle}>날짜(시각)</th>
                                            <th style={thStyle}>결과</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {l7rows.map((row, i) => (
                                            <tr key={`${row.pid}_${row.kind}_${row.refId || ''}_${row.day || ''}_${row.at || ''}_${i}`}>
                                                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                    {row.pid ? (
                                                        <button type="button" onClick={() => navigate(`/patients/${row.pid}#notices`)} style={linkBtn}>{row.name || '(이름 없음)'}</button>
                                                    ) : (
                                                        <button type="button" onClick={() => navigate('/patients/alerts#status')} style={linkBtn}>{Number.isFinite(row.targetCount) ? `대상 ${row.targetCount}명` : '대상 환자'}</button>
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={{ color: '#0f172a' }}>{rowLabelOf(row)}</div>
                                                    {row.stageLabel && <div style={{ fontSize: 12, color: '#64748b' }}>{row.stageLabel}</div>}
                                                </td>
                                                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{Number.isFinite(row.at) ? fmtAt(row.at) : fmtDayKey(row.day)}</td>
                                                <td style={tdStyle}>{row.resultLabel || row.eventLabel || ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {last7.data?.truncated && <div style={{ padding: '6px 12px', fontSize: 12, color: '#94a3b8' }}>{'표시 한도를 넘어 일부 행이 보이지 않습니다.'}</div>}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ClinicUpcomingList;
